// --- Field fragments ---

const STATEMENT_UPLOAD_FIELDS = `
  id publicId status originalFilename byteSize
  uploader { id name }
  submittedAt scheduledFor startedAt finishedAt
  processedCount failedCount
  rescheduledAt rescheduledBy { id name } rescheduleReason
  canceledAt canceledBy { id name } cancelReason
  reverseRequestedAt reverseRequestedBy { id name } reverseReason reversedAt
  errorSummary validatedAt validationErrors
  createdAt isInceptionToDate needsRerun totalAmount totalUnits
  statementUploadSheets { id sheetName productId productName fundCode valueDate rowCount sheetSha256 paymentStatus units amount price }
`;

// --- Queries ---

export const GET_STATEMENT_UPLOADS = `
  query statementUploads($filters: UploadFiltersInput, $q: String, $page: Int, $pageSize: Int) {
    statementUploads(filters: $filters, q: $q, page: $page, pageSize: $pageSize) {
      results { ${STATEMENT_UPLOAD_FIELDS} }
      count errors { property description }
    }
  }
`;

export const GET_STATEMENT_UPLOAD = `
  query statementUpload($id: ID!) {
    statementUpload(id: $id) { 
      ${STATEMENT_UPLOAD_FIELDS}
      issueEntries {
        id statementUploadId priceDate paymentsPriceDate matchStatus
        uploadAmount paymentsAmount uploadUnits paymentsUnits
        expectedPaymentCount matchedPaymentCount missedPaymentCount unitDiscrepancies
        diffAmount diffUnits txCount reversedAt createdAt
        taCheckResult taCheckedAt runType companiesCount
        companies {
          companyId companyName uploadAmount paymentsAmount uploadUnits paymentsUnits diffAmount diffUnits matchStatus employeeAggregateMatches
          contributions {
            id memberId memberName uploadAmount paymentsAmount uploadUnits paymentsUnits diffAmount diffUnits narration
          }
        }
        product { id name fundCode }
        price { id amount }
      }
    }
  }
`;

export const GET_STATEMENT_UPLOAD_AUDIT_EVENTS = `
  query statementUploadAuditEvents($id: ID!) {
    statementUploadAuditEvents(id: $id) {
      id action actor { id name } details createdAt
    }
  }
`;

export const GET_RECONCILIATION_ENTRIES = `
  query reconciliationEntries($filters: ReconciliationFiltersInput, $page: Int, $pageSize: Int) {
    reconciliationEntries(filters: $filters, page: $page, pageSize: $pageSize) {
      results {
        id statementUploadId priceDate paymentsPriceDate needsRerun
        statementUpload { id publicId needsRerun }
        product { id name fundCode }
        price { id amount }
        uploadAmount paymentsAmount uploadUnits paymentsUnits
        expectedPaymentCount matchedPaymentCount missedPaymentCount unitDiscrepancies
        txCount matchStatus diffAmount diffUnits reversedAt createdAt
        runType companiesCount supersededCount remarkLines { text tone }
      }
      count matchedCount mismatchedCount pendingCount
      summary { entries uploadAmount paymentsAmount uploadUnits paymentsUnits txCount reversed }
      uploadSummary {
        entries matchedCount mismatchedCount pendingCount uploadAmount paymentsAmount txCount productCount reconciledAt
      }
      productOptions { id name }
      lastRunAt lastRunEntries
      errors { property description }
    }
  }
`;

export const GET_RECONCILIATION_ROW_EXPLANATIONS = `
  query reconciliationEntry($id: ID!) {
    reconciliationEntry(id: $id) {
      id
      rowExplanations {
        statementUploadRowId rowNumber reason headline nextStep rowAmount rowUnits
        lines { label value compareTo highlight }
        narration
        classes { label count amount units payments { taId details reference amount units price } }
      }
    }
  }
`;

export const GET_RECONCILIATION_TRANSACTIONS = `
  query reconciliationTransactions($productId: ID!, $priceDate: ISO8601Date!, $statementUploadId: ID, $limit: Int) {
    reconciliationTransactions(productId: $productId, priceDate: $priceDate, statementUploadId: $statementUploadId, limit: $limit) {
      id statementUploadSheetId rowNumber valueDate
      transactionDate transactionAmount units drcr transactionReference transactionDetails
      deviated pending parseError
    }
  }
`;

export const GET_RECONCILIATION_MISSED_PAYMENTS = `
  query reconciliationMissedPayments($productId: ID!, $priceDate: ISO8601Date!, $statementUploadId: ID, $limit: Int) {
    reconciliationMissedPayments(productId: $productId, priceDate: $priceDate, statementUploadId: $statementUploadId, limit: $limit) {
      id
      transactionDate
      valueDate
      transactionAmount
      drcr
      taId
      transactionDetails
    }
  }
`;

// --- Mutations ---

export const UPLOAD_FILE = `
  mutation uploadFile($data: String!, $extension: String!, $source: String, $fileType: String, $skipJobProcess: Boolean) {
    uploadFile(data: $data, extension: $extension, source: $source, type: $fileType, skipJobProcess: $skipJobProcess) {
      url name fileUploadId errors { property description }
    }
  }
`;

export const UPLOAD_STATEMENT = `
  mutation uploadStatement($filename: String!, $base64Contents: String!, $byteSize: Int!, $isInceptionToDate: Boolean) {
    uploadStatement(filename: $filename, base64Contents: $base64Contents, byteSize: $byteSize, isInceptionToDate: $isInceptionToDate) {
      upload { ${STATEMENT_UPLOAD_FIELDS} }
      errors { code details }
    }
  }
`;

export const REPLACE_STATEMENT_UPLOAD_FILE = `
  mutation replaceStatementUploadFile($id: ID!, $fileUploadId: ID!) {
    replaceStatementUploadFile(id: $id, fileUploadId: $fileUploadId) {
      upload { ${STATEMENT_UPLOAD_FIELDS} }
      errors { code details }
    }
  }
`;

export const RESCHEDULE_STATEMENT_UPLOAD = `
  mutation rescheduleStatementUpload($id: ID!, $scheduledFor: ISO8601DateTime!, $reason: String!) {
    rescheduleStatementUpload(id: $id, scheduledFor: $scheduledFor, reason: $reason) {
      upload { id scheduledFor rescheduledAt rescheduledBy { id name } }
      errors { property description }
    }
  }
`;

export const CANCEL_STATEMENT_UPLOAD = `
  mutation cancelStatementUpload($id: ID!, $reason: String!) {
    cancelStatementUpload(id: $id, reason: $reason) {
      upload { id status canceledAt cancelReason canceledBy { id name } }
      errors { property description }
    }
  }
`;

export const RETRY_STATEMENT_UPLOAD = `
  mutation retryStatementUpload($id: ID!) {
    retryStatementUpload(id: $id) {
      upload { id status scheduledFor }
      errors { property description }
    }
  }
`;

export const REVERSE_STATEMENT_UPLOAD = `
  mutation reverseStatementUpload($id: ID!, $reason: String!) {
    reverseStatementUpload(id: $id, reason: $reason) {
      upload { id status reverseRequestedAt reverseRequestedBy { id name } reverseReason }
      errors { property description }
    }
  }
`;

export const EXPEDITE_STATEMENT_UPLOAD = `
  mutation expediteStatementUpload($id: ID!, $reason: String!) {
    expediteStatementUpload(id: $id, reason: $reason) {
      upload { id status scheduledFor }
      errors { property description }
    }
  }
`;

export const DOWNLOAD_STATEMENT_UPLOAD_BUNDLE = `
  mutation downloadStatementUploadBundle($id: ID!) {
    downloadStatementUploadBundle(id: $id) {
      zipBase64
      filename
      errors { property description }
    }
  }
`;

export const GET_RECONCILIATION_ENTRY_COMPANIES = `
  query reconciliationEntry($id: ID!) {
    reconciliationEntry(id: $id) {
      id
      companies {
        companyId companyName uploadAmount paymentsAmount uploadUnits paymentsUnits diffAmount diffUnits matchStatus employeeAggregateMatches
        contributions {
          id memberId memberName uploadAmount paymentsAmount uploadUnits paymentsUnits diffAmount diffUnits narration
        }
      }
    }
  }
`;

export const GET_RECONCILIATION_ENTRY_DRIFT = `
  query reconciliationEntry($id: ID!) {
    reconciliationEntry(id: $id) {
      id priceAmount
      supersededEntries {
        id createdAt reversedAt priceAmount matchStatus runType
        uploadAmount paymentsAmount uploadUnits paymentsUnits
        diffAmount diffUnits txCount
        expectedPaymentCount matchedPaymentCount missedPaymentCount unitDiscrepancies
        statementUpload { id publicId originalFilename }
        transactionDrift {
          comparable
          price { previous current changed }
          changes {
            paymentId paymentTaId statementUploadRowId change
            previousAmount previousUnits previousDetails previousTransactionDate previousStatus previousShareClass
            currentAmount currentUnits currentDetails currentTransactionDate currentStatus currentShareClass
          }
        }
      }
      transactionDrift {
        comparable
        price { previous current changed }
        changes {
          paymentId paymentTaId statementUploadRowId change
          previousAmount previousUnits previousDetails previousTransactionDate previousStatus previousShareClass
          currentAmount currentUnits currentDetails currentTransactionDate currentStatus currentShareClass
        }
      }
    }
  }
`;

export const GET_UPLOAD_RERUN_DRIFT = `
  query statementUpload($id: ID!) {
    statementUpload(id: $id) {
      id publicId
      entriesNeedingRerun {
        id priceDate priceAmount matchStatus txCount
        product { id name fundCode }
        pendingDrift {
          comparable
          price { previous current changed }
          changes {
            paymentId paymentTaId statementUploadRowId change
            previousAmount previousUnits previousDetails previousTransactionDate previousStatus previousShareClass
            currentAmount currentUnits currentDetails currentTransactionDate currentStatus currentShareClass
          }
        }
      }
    }
  }
`;

export const TRIGGER_RECONCILIATION = `
  mutation triggerReconciliation($uploadId: ID!, $productIds: [ID!]) {
    triggerReconciliation(uploadId: $uploadId, productIds: $productIds) {
      upload {
        id status needsRerun
      }
      errors { property description }
    }
  }
`;

export const GET_RECONCILIATION_NOTIFICATIONS = `
  query reconciliationNotifications($uploadId: ID) {
    reconciliationNotifications(uploadId: $uploadId) {
      id
      action
      createdAt
      actor { id name }
      details
      statementUpload {
        id
        publicId
        originalFilename
        statementUploadSheets {
          id
          productName
          fundCode
        }
      }
    }
  }
`;
